import { useEffect, useMemo } from "react";
import { v4 as uuid } from "uuid";

import { buildPreviewSrcDoc } from "../helpers/build-preview-srcdoc";
import { useIframePreviewBridge } from "./use-iframe-preview-bridge";

import { IframePreviewBridge } from "../types";

interface UsePreviewSrcDocArgs {
  htmlContent: string;
  htmlJSON: string;
  isCommentMode: boolean;
  isFullContentMode: boolean;
  mobileView: boolean;
  touchpointName?: string;
  baseHref?: string;
}

interface PreviewLabels {
  regionLabel: string;
  iframeTitle: string;
  statusMessage: string;
}

type UsePreviewSrcDocResult = Pick<
  IframePreviewBridge,
  "iframeRef" | "modules" | "contentHeight"
> & {
  srcDoc: string;
  documentKey: string;
  labels: PreviewLabels;
};

/**
 * Builds the preview `srcDoc` (comment mode and Full content each turn author-script execution off), owns
 * the postMessage bridge, and derives the a11y region/title/status labels. Keeps the view a thin
 * presentational shell.
 */
export const usePreviewSrcDoc = ({
  htmlContent,
  htmlJSON,
  isCommentMode,
  isFullContentMode,
  mobileView,
  touchpointName,
  baseHref,
}: UsePreviewSrcDocArgs): UsePreviewSrcDocResult => {
  const { nonce, iframeRef, modules, contentHeight, status, reset } = useIframePreviewBridge();

  const srcDoc = useMemo(
    () =>
      buildPreviewSrcDoc({
        finalHtml: htmlContent,
        htmlJSON,
        nonce,
        // Both modes render statically, but only comment mode blocks links — in Full content the reviewer
        // sees the whole quiz at once AND can still click through its links.
        executeScripts: !isCommentMode && !isFullContentMode,
        allowLinks: !isCommentMode,
        baseHref,
      }),
    [htmlContent, htmlJSON, nonce, isCommentMode, isFullContentMode, baseHref],
  );

  /**
   * Remount key for the iframe, not a cache key — it has to change in the *same* render as `srcDoc`.
   *
   * Assigning `srcdoc` on a live iframe navigates its browsing context, and that appends an entry to the
   * **joint session history** the top-level document shares. Measured: three comment-mode toggles took
   * `history.length` from 2 to 5, so `navigate(-1)` behind the Back button walked those entries instead of
   * leaving the page. Mounting a *fresh* element instead keeps the count flat, because a new browsing
   * context's first navigation replaces its initial `about:blank` rather than pushing.
   */
  const documentKey = useMemo(() => uuid(), [srcDoc]);

  // The iframe reloads whenever srcDoc changes (e.g. toggling comment mode); reset the module rects and
  // status for the new document (see use-iframe-preview-bridge for why contentHeight itself is untouched).
  useEffect(() => {
    reset();
  }, [srcDoc, reset]);

  const labels = useMemo<PreviewLabels>(() => {
    const viewLabel = mobileView ? "mobile" : "desktop";
    return {
      regionLabel: touchpointName ? `${touchpointName} preview` : "Creative preview",
      iframeTitle: touchpointName
        ? `Preview of ${touchpointName} — ${viewLabel}`
        : `Creative preview — ${viewLabel}`,
      statusMessage:
        status === "error"
          ? "Creative preview failed to load."
          : status === "ready"
            ? "Creative preview loaded."
            : "Loading creative preview…",
    };
  }, [mobileView, touchpointName, status]);

  return { srcDoc, documentKey, iframeRef, modules, contentHeight, labels };
};
